import { TICKS_PER_LOOP, TransportStates } from '@arpeggi/shared'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import React, { FC, MouseEventHandler, MutableRefObject, useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { IClipStore } from '../../../stores/song/clip/ClipStore'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { Box, Column, Row, Text } from '../../core/Components'
import { blue5, getStemColor, gray10, gray15, gray5 } from '../../platform/color'
import AudioVisualizer from '../../player/AudioVisualizer'
import Dropzone from '../../studio/Dropzone'
import { StudioDraggableTypes } from '../../studio/StudioUtils'
import { ClipHandle } from '../tracks/AudioStemTrack'

const MARGIN_HEIGHT = '30px'

const EditorBody = styled(Row, {
  width: '100%',
  height: '100%',
  paddingTop: '25px',
  paddingBottom: '40px',
  alignItems: 'stretch',
  justifyContent: 'center',
  variants: {
    useScissorCursor: {
      true: {
        cursor: 'crosshair'
      }
    }
  }
})

/** a heuristic created to find the optimal scale of the lines in the editor */
const getLinesScaleFactor = (lengthInTicks: number): number => {
  const lengthInLoops = lengthInTicks / TICKS_PER_LOOP
  if (lengthInLoops < 0.25) return 0.5
  if (lengthInLoops < 1) return 1

  for (let power = 0; power < 10; power++) {
    if (Math.pow(2, power) <= lengthInLoops && lengthInLoops < Math.pow(2, power + 1)) {
      return Math.pow(2, power + 1)
    }
  }
  return 1
}

/** Note: This is not 16th notes, but the 16th of a section */
const ticksToSection16ths = (ticks: number) => {
  return (16 * ticks) / TICKS_PER_LOOP
}

const useClipEditor = (stemIndex: number) => {
  const containerRef = useRef<HTMLDivElement>()
  const flags = useFlags()
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer>(null)
  const { studio } = useTreeState()
  const stem = studio.song.stems[stemIndex] as IAudioStemStore
  const color = getStemColor(stem)
  const currentClip = stem.getClipByID(studio.selectedClipIDs[0])
  const tempo = studio.song.tempo
  const songLengthIn48n = studio.song.songLengthIn48n
  const containerWidth = containerRef.current?.offsetWidth

  const acceptedAudioStemTypes = [StudioDraggableTypes.SAMPLE]
  if (flags.stemRemixingAsClips) {
    acceptedAudioStemTypes.push(StudioDraggableTypes.STEM)
  }
  // For dropping a new sample
  const [{ isOver: isOverAudioDrop }, audioDrop] = useDrop(
    () => ({
      accept: acceptedAudioStemTypes,
      drop: (item: any) => {
        if (item.type === StudioDraggableTypes.SAMPLE) {
          console.log('dropped a new sample: ', item.sample)
          studio.addClipFromSampleToAudioStem(item.sample, stemIndex, 0).then(() => {
            studio.setSelectedClipID(stem.clips[stem.clips.length - 1].uniqueId)
          })
        } else if (item.type === StudioDraggableTypes.STEM) {
          console.log('dropped a new stem as audio clip: ', item.stem.id)
          studio.addClipFromStemToAudioStem(item.stem.id, stemIndex, 0).then(() => {
            studio.setSelectedClipID(stem.clips[stem.clips.length - 1].uniqueId)
          })
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    [acceptedAudioStemTypes, stemIndex, stem]
  )

  useEffect(() => {
    if (!currentClip) return
    currentClip.sampleWithEffects.getSampleBuffer().then((ab) => setAudioBuffer(ab))
  }, [currentClip, currentClip?.sampleWithEffects.reverse])

  let lengthInTicks: number = currentClip?.getAudioSourceDuration48n(tempo)

  let clipPercent: number
  let offsetPercent: number
  let moveLeft: (deltaPX: number) => boolean
  let moveRight: (deltaPX: number) => boolean
  let onClick: MouseEventHandler<HTMLDivElement>
  if (currentClip && containerRef.current) {
    clipPercent =
      ((currentClip.endInComposition48n -
        currentClip.startInComposition48n +
        currentClip.offset48n) /
        currentClip.getAudioSourceDuration48n(tempo)) *
      100
    offsetPercent = (currentClip.offset48n / currentClip.getAudioSourceDuration48n(tempo)) * 100

    moveLeft = (deltaPX) => {
      return currentClip.moveLeftSide48n((deltaPX / containerWidth) * lengthInTicks, tempo)
    }

    moveRight = (deltaPX) => {
      return currentClip.moveRightSide48n(
        (deltaPX / containerWidth) * lengthInTicks,
        tempo,
        songLengthIn48n
      )
    }

    onClick = (e) => {
      if (studio.mouseAction !== 'CUT') return

      studio.setMouseAction('DEFAULT')
      const parentStemIndex = studio.song.getClipParentStemIndex(currentClip.uniqueId)
      const audioStem = studio.song.stems[parentStemIndex] as IAudioStemStore
      const clickPercent =
        (e.pageX - containerRef.current.getBoundingClientRect().left) /
        containerRef.current.offsetWidth
      const clickTicks = clickPercent * currentClip.getAudioSourceDuration48n(studio.song.tempo)
      audioStem.cutClipAtTick(currentClip.uniqueId, clickTicks, studio.song.tempo)
      return true
    }
  }

  return {
    audioBuffer,
    containerRef,
    currentClip,
    color,
    lengthInTicks,
    offsetPercent,
    moveLeft,
    moveRight,
    clipPercent,
    onClick,
    audioDrop,
    isOverAudioDrop
  }
}

interface CutLineProps {
  containerRef: React.RefObject<HTMLDivElement>
}
const CutLine: FC<CutLineProps> = observer(({ containerRef }) => {
  const { studio } = useTreeState()
  const [left, setLeft] = useState<number>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const mouseMove = (e: MouseEvent) => {
      setLeft(e.pageX - containerRef.current.getBoundingClientRect().left)
    }
    document.addEventListener('mousemove', mouseMove)
    return () => document.removeEventListener('mousemove', mouseMove)
  }, [containerRef.current])

  if (studio.mouseAction !== 'CUT') return
  return (
    <Box
      css={{
        width: '1px',
        height: '100%',
        backgroundColor: blue5,
        position: 'absolute',
        bottom: 0,
        left
      }}
    />
  )
})

interface ClipContainerProps {
  currentClip: IClipStore | null
  audioBuffer: AudioBuffer
  color: string
  children: any
  onClick: MouseEventHandler<HTMLDivElement>
}
const ClipContainer = React.forwardRef(
  (
    { currentClip, audioBuffer, color, children, onClick }: ClipContainerProps,
    ref: MutableRefObject<HTMLDivElement>
  ) => (
    <Column
      ref={ref}
      onClick={onClick}
      verticalCenter
      horizontalCenter
      css={{
        paddingBottom: MARGIN_HEIGHT,
        position: 'relative',
        flex: '1',
        alignItems: 'stretch',
        backgroundColor: currentClip && audioBuffer && color,
        borderRadius: '11px',
        overflow: 'hidden',
        boxShadow: 'inset 0px 0px 11px -4px rgba(0, 0, 0, 0.25), inset 0px 0px 4px #FFFFFF',
        opacity: !!currentClip ? '100%' : '0%'
      }}
    >
      {audioBuffer && children}
    </Column>
  )
)

interface EditorResizeHandleProps {
  side: 'left' | 'right'
  offset: number
  onMove: (newValue: number) => boolean
  clipLengthInTicks: number
}
const EditorResizeHandle: FC<EditorResizeHandleProps> = ({
  side,
  offset,
  onMove,
  clipLengthInTicks
}) => (
  <Row
    css={{
      position: 'absolute',
      height: '100%',
      zIndex: '5',
      left: side === 'left' && '0',
      right: side === 'right' && '0',
      opacity: '40%',
      flexDirection: side === 'left' ? 'row' : 'row-reverse',
      justifyContent: side === 'left' ? 'start' : 'end',
      marginTop: MARGIN_HEIGHT,
      overflow: 'visible',
      // adding 2px as that's the size of the clip handle
      width: `calc(${side === 'left' ? offset : 100 - offset}% + 2px)`
    }}
  >
    {/* we use both a row and the handle so that the handle will always show up */}
    <Row
      css={{
        width: '100%',
        height: '100%',
        backgroundColor: gray10,
        position: 'relative'
      }}
    />
    <ClipHandle onMove={onMove} css={{ position: 'relative' }} dependencies={[clipLengthInTicks]} />
  </Row>
)

interface ClipTimelineProps {
  lengthInTicks: number
  offsetTicks: number
  startInSongTicks: number
}
const ClipTimeline: FC<ClipTimelineProps> = ({ lengthInTicks, offsetTicks, startInSongTicks }) => {
  const container = useRef<HTMLDivElement>(null)
  const scale = getLinesScaleFactor(lengthInTicks)
  const lengthIn16ths = ticksToSection16ths(lengthInTicks - offsetTicks)
  const startOffset16ths = ticksToSection16ths(startInSongTicks)
  const startOffset16thsFloored = Math.floor(startOffset16ths)
  const startOffsetTicks = startInSongTicks - (startOffset16thsFloored * TICKS_PER_LOOP) / 16
  const startOffsetPercent = (100 * startOffsetTicks) / lengthInTicks
  return (
    <Row
      css={{
        height: MARGIN_HEIGHT,
        minWidth: '100%',
        zIndex: '4',
        overflow: 'hidden',
        paddingLeft: `${-startOffsetPercent + (100 * offsetTicks) / lengthInTicks}%`
      }}
      ref={container}
      verticalCenter
    >
      {Array.from({ length: Math.ceil(lengthIn16ths / scale) })
        .fill(0)
        .map((_, index: number) => {
          return (
            <Row
              css={{
                height: '100%',
                minWidth: `calc(${100 / (lengthIn16ths / scale)}%)`,
                overflow: 'visible'
              }}
              key={index}
              justifyContentEnd
              verticalCenter
            >
              {Array.from({ length: 4 })
                .fill(0)
                .map((_, i) => (
                  <Box
                    css={{
                      marginRight: 'calc(20% - 1px)',
                      width: '1px',
                      height: '6px',
                      backgroundColor: gray5
                    }}
                    key={i}
                  />
                ))}
              <Row css={{ width: '1px', textAlign: 'center' }} key={index} horizontalCenter>
                <Text css={{ padding: '0px', margin: '0px', fontSize: '12px', color: gray10 }}>
                  {4 / scale < 1
                    ? `${
                        (((index + startOffset16thsFloored + 1) % (4 / scale)) + 1) / (4 / scale)
                      }/1`
                    : `${((index + startOffset16thsFloored + 1) % (4 / scale)) + 1}/${4 / scale}`}
                </Text>
              </Row>
            </Row>
          )
        })}
    </Row>
  )
}

interface EditorLinesProps {
  lengthInTicks: number
  offsetTicks: number
  startInSongTicks: number
}
const EditorLines: FC<EditorLinesProps> = ({ lengthInTicks, offsetTicks, startInSongTicks }) => {
  const scale = getLinesScaleFactor(lengthInTicks)
  const lengthIn16ths = ticksToSection16ths(lengthInTicks - offsetTicks)
  const startOffset16ths = ticksToSection16ths(startInSongTicks)
  const startOffset16thsFloored = Math.floor(startOffset16ths)
  const startOffsetTicks = startInSongTicks - (startOffset16thsFloored * TICKS_PER_LOOP) / 16
  const startOffsetPercent = (100 * startOffsetTicks) / lengthInTicks
  return (
    <Row
      css={{
        position: 'absolute',
        height: '100%',
        width: '100%',
        zIndex: 4,
        bottom: '0',
        paddingLeft: `${(100 * offsetTicks) / lengthInTicks - startOffsetPercent}%`
      }}
    >
      {Array.from({ length: Math.floor(lengthIn16ths / scale) })
        .fill(0)
        .map((_, index: number) => {
          return (
            <Box
              css={{
                marginTop: MARGIN_HEIGHT,
                height: '90%',
                width: '1px',
                marginLeft: `calc(${100 / (lengthIn16ths / scale)}% - 1px)`,
                backgroundColor: gray5
              }}
              key={index}
            />
          )
        })}
    </Row>
  )
}

interface ClipEditorPlayheadProps {
  currentClip: IClipStore
  clipLengthInTicks: number
}
const ClipEditorPlayhead: FC<ClipEditorPlayheadProps> = observer(
  ({ currentClip, clipLengthInTicks }) => {
    const { alternatePlayer: studio } = useTreeState()
    if (!currentClip || studio.playheadVisiblePositionInTicks < currentClip.startInComposition48n) {
      return null
    }
    const left =
      (studio.playheadVisiblePositionInTicks -
        currentClip.startInComposition48n +
        currentClip.offset48n) /
      clipLengthInTicks
    if (studio.transportState !== TransportStates.PLAYING) return null
    return (
      <Box
        css={{
          marginTop: MARGIN_HEIGHT,
          position: 'absolute',
          height: '100%',
          width: '1px',
          backgroundColor: gray15,
          left: `${left * 100}%`
        }}
      />
    )
  }
)

interface Props {
  stemIndex: number
}
const ClipEditor: FC<Props> = ({ stemIndex }) => {
  const { studio } = useTreeState()
  const {
    containerRef,
    currentClip,
    audioBuffer,
    color,
    lengthInTicks,
    offsetPercent,
    moveLeft,
    moveRight,
    clipPercent,
    onClick,
    audioDrop,
    isOverAudioDrop
  } = useClipEditor(stemIndex)

  if (studio.selectedClipIDs.length === 0 || !currentClip) {
    return (
      <Dropzone
        icon={<Text css={{ margin: 0 }}>Drag and drop a sound</Text>}
        dropzoneActivated={isOverAudioDrop}
        ref={audioDrop}
      />
    )
  }
  if (studio.selectedClipIDs.length > 1) {
    return (
      <Row css={{ width: '100%', height: '100%' }} verticalCenter horizontalCenter>
        <Text>{`${studio.selectedClipIDs.length} clips selected`}</Text>
      </Row>
    )
  }
  return (
    <EditorBody useScissorCursor={studio.mouseAction === 'CUT'}>
      <ClipContainer
        ref={containerRef}
        currentClip={currentClip}
        audioBuffer={audioBuffer}
        color={color}
        onClick={onClick}
      >
        <ClipEditorPlayhead currentClip={currentClip} clipLengthInTicks={lengthInTicks} />

        <ClipTimeline
          lengthInTicks={lengthInTicks}
          // startInSongTicks={currentClip?.startInComposition48n || 0} // Add this back to make the timeline offset by position in the song
          startInSongTicks={0}
          offsetTicks={currentClip?.offset48n || 0}
        />
        <EditorResizeHandle
          side="left"
          onMove={moveLeft}
          offset={offsetPercent}
          clipLengthInTicks={lengthInTicks}
        />
        <AudioVisualizer
          audioBuffer={audioBuffer}
          options={{
            color: lengthInTicks / TICKS_PER_LOOP > 3 ? '#444444' : '#000000',
            segmentWidth: lengthInTicks / TICKS_PER_LOOP > 3 ? 0.8 : 0.4,
            segmentSpacing: 0,
            resolution: 2
          }}
        />
        <EditorResizeHandle
          side="right"
          onMove={moveRight}
          offset={clipPercent}
          clipLengthInTicks={lengthInTicks}
        />

        {/* Line that shows up where the cut would be */}
        <CutLine containerRef={containerRef} />

        {/* The lines that show up in the body of the editor, below the timeline */}
        <EditorLines
          lengthInTicks={lengthInTicks}
          // startInSongTicks={currentClip?.startInComposition48n || 0} // Add this back to make the timeline offset by position in the song
          startInSongTicks={0}
          offsetTicks={currentClip?.offset48n || 0}
        />
      </ClipContainer>
    </EditorBody>
  )
}

export default observer(ClipEditor)
